import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { StepsTooltip } from '../types';
import { renderRichText } from '../utils/stepsTooltip';
import { usePopupHost, useFullscreenOverlayHost } from '../PopupHostContext';

type Props = {
  data: StepsTooltip;
  /** 打开时定位到的平台 / 步骤（来自小卡点击的图片）。 */
  initialPlatformIndex?: number;
  initialStepIndex?: number;
  onClose: () => void;
};

/**
 * steps 操作引导大图预览（点击「查看教程」或小卡图片后全屏弹层）。
 *
 * 与 V1 core/tooltipController.tsx 的 StepsBigPreview 视觉 1:1（复用 .rte-steps-preview-* CSS），
 * 但状态自管：平台 / 步骤索引住在组件内部 state（由 initial* 播种），
 * 不像 V1 由外层 useTooltipController 把 index + onSwitchPlatform/onStepChange 全量透传——
 * 节点接入只需「开/关 + 从哪张图打开」，把切换逻辑收进 overlay 更干净（V2 逻辑，非 1:1 搬 V1 管线）。
 *
 * createPortal 目标按优先级：消费端指定的全屏容器（FullscreenOverlayHostContext，与业务自己的
 * 全屏蒙层同挂点）→ 浮层宿主 .rte-popup-host（Qiankun 子应用内仍是子应用 DOM、非沙箱代理的 body）
 * → document.body 兜底。挂 host 时靠 overlay 自身 fixed 铺满视口，0 尺寸 host 不影响铺满。
 */
export const StepsPreviewOverlay: React.FC<Props> = ({
  data,
  initialPlatformIndex = 0,
  initialStepIndex = 0,
  onClose,
}) => {
  const popupHost = usePopupHost();
  // 优先挂消费端指定的页面级容器（与业务全屏蒙层同挂点）；未指定时维持原 host 行为。
  // 移出 host 后 .rte-popup-host[data-theme] 祖先选择器失效，故根节点自带 data-theme
  //（从常驻的 popupHost 读取），配套 CSS 见 index.css 的 [data-theme] 伴生选择器。
  const fullscreenHost = useFullscreenOverlayHost();
  const overlayTheme = popupHost?.dataset.theme ?? 'light';
  const platforms = Array.isArray(data.platforms) ? data.platforms : [];
  const platformCount = platforms.length;

  const [platformIndex, setPlatformIndex] = useState(initialPlatformIndex);
  const [stepIndex, setStepIndex] = useState(initialStepIndex);

  const pIdx = Math.min(Math.max(platformIndex, 0), Math.max(platformCount - 1, 0));
  const currentPlatform = platforms[pIdx];
  const images = Array.isArray(currentPlatform?.images) ? currentPlatform.images : [];
  const stepDescs = Array.isArray(currentPlatform?.stepDescriptions)
    ? currentPlatform.stepDescriptions
    : [];
  const total = Math.min(images.length, stepDescs.length);
  const idx = Math.min(Math.max(stepIndex, 0), Math.max(total - 1, 0));
  const currentSrc = images[idx];
  const stepText = stepDescs[idx] || '';
  const description = (data.description || '').trim();
  const disclaimer = (data.disclaimer || '').trim();
  const showTabs = platformCount > 1;

  // ESC 关闭 + 左右方向键切步骤（捕获阶段，避免被编辑器内部按键处理拦截）
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && idx > 0) setStepIndex(idx - 1);
      else if (e.key === 'ArrowRight' && idx < total - 1) setStepIndex(idx + 1);
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [idx, total, onClose]);

  const switchPlatform = (i: number) => {
    if (i === pIdx) return;
    setPlatformIndex(i);
    setStepIndex(0);
  };

  return createPortal(
    <div
      className={
        'rte-steps-preview-overlay' +
        // 挂消费端容器时切 absolute（--contained）：随容器定位而非 fixed 铺满视口，
        // 与业务全屏蒙层同语义——Qiankun 布局下不盖住容器外的基座侧边栏/页面留白。
        (fullscreenHost ? ' rte-steps-preview-overlay--contained' : '')
      }
      data-theme={overlayTheme}
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        // 仅点到 overlay 背景空白才关闭；点内部任何元素（含步骤文字里的紫色跳转胶囊）保持打开
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="rte-steps-preview-stage" onClick={(e) => e.stopPropagation()}>
        <div className="rte-steps-preview-header">
          <span className="rte-steps-preview-title">{data.title}</span>
          <button
            type="button"
            aria-label="关闭"
            className="rte-steps-preview-close"
            onClick={onClose}
          >
            <i className="iconfont icon-guanbi" aria-hidden="true" />
          </button>
        </div>

        <div className="rte-steps-preview-body">
          {showTabs || description ? (
            <div
              className={
                'rte-steps-preview-top-row' +
                (!showTabs && description ? ' is-description-only' : '')
              }
            >
              {showTabs ? (
                <div className="rte-steps-preview-platform-tabs" role="tablist">
                  {platforms.map((p, i) => (
                    <button
                      key={`${p.label}-${i}`}
                      type="button"
                      role="tab"
                      aria-selected={i === pIdx}
                      className={
                        'rte-steps-preview-platform-tab' + (i === pIdx ? ' is-active' : '')
                      }
                      onClick={() => switchPlatform(i)}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
              ) : null}
              {description ? (
                <div className="rte-steps-preview-description">
                  {renderRichText(description)}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="rte-steps-preview-image-wrap">
            {currentSrc ? (
              <img
                className="rte-steps-preview-image"
                src={currentSrc}
                alt={`step-${idx + 1}`}
              />
            ) : null}

            {total > 0 ? (
              <div className="rte-steps-preview-step-badge">
                <span>步骤</span>
                <span>
                  {idx + 1}/{total}
                </span>
              </div>
            ) : null}

            {total > 1 && idx > 0 ? (
              <button
                type="button"
                aria-label="上一步"
                className="rte-steps-preview-nav rte-steps-preview-nav-prev"
                onClick={() => setStepIndex(idx - 1)}
              >
                <i className="iconfont icon-qiehuan-xiayizhang" aria-hidden="true" />
              </button>
            ) : null}
            {total > 1 && idx < total - 1 ? (
              <button
                type="button"
                aria-label="下一步"
                className="rte-steps-preview-nav rte-steps-preview-nav-next"
                onClick={() => setStepIndex(idx + 1)}
              >
                <i className="iconfont icon-qiehuan-xiayizhang" aria-hidden="true" />
              </button>
            ) : null}

            {total > 1 ? (
              <div className="rte-steps-preview-dots" role="presentation">
                {Array.from({ length: total }).map((_, i) => (
                  <span
                    key={i}
                    className={'rte-steps-preview-dot' + (i === idx ? ' is-active' : '')}
                  />
                ))}
              </div>
            ) : null}
          </div>

          {stepText ? (
            <div className="rte-steps-preview-step-text">{renderRichText(stepText)}</div>
          ) : null}

          {disclaimer ? (
            <div className="rte-steps-preview-warning">
              <i
                className="iconfont icon-jinggao1 rte-steps-preview-warning-icon"
                aria-hidden="true"
              />
              <span>{disclaimer}</span>
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    fullscreenHost ?? popupHost ?? document.body,
  );
};
